import React from "react";
import { Container, Overline, Reveal, HairlineList } from "@/components/primitives";
import { ContentSection, ProductFinalCTA } from "@/components/ProductSections";
import { useDocumentMeta } from "@/lib/meta";

export default function Founder() {
  useDocumentMeta({
    title: "Founder story",
    description:
      "Beyond the Box was built from inside a working law firm — to solve real operational, compliance and commercial pain, and to give owners freedom from dependency.",
    path: "/founder",
  });

  return (
    <div data-testid="founder-page">
      {/* Hero */}
      <section className="border-b border-border">
        <Container className="py-20 md:py-28">
          <Reveal>
            <Overline accent>Founder story · Why we built this</Overline>
            <h1 className="mt-6 max-w-4xl font-display text-5xl font-bold leading-[0.92] tracking-[-0.03em] text-foreground md:text-8xl">
              Built from inside the problem.
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              These products were not imagined from the outside. They were built because real
              law firms were living with genuine operational, compliance and commercial pain.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* Pillar 1 — real operational pain */}
      <ContentSection
        n="01"
        eyebrow="Built from real operational pain"
        title="Not a whiteboard idea."
        testid="founder-pain"
      >
        <p className="text-lg leading-relaxed text-foreground">
          These products were built to solve problems that owner-managed law firms actually face:
          software that forces them into dependency, compliance support that is expensive but
          unreliable, and operational systems that do not reflect how real teams work.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          The aim was not to add more noise, but to create tools that give firms more control,
          better visibility, and a cleaner path to doing the right things well.
        </p>
        <HairlineList
          testid="founder-pain-list"
          items={[
            "Built to solve problems that owner-managed firms actually live with.",
            "Shaped by real operational need, not generic SaaS assumptions.",
            "Created because too many firms are still forced into expensive, opaque dependencies.",
          ]}
        />
      </ContentSection>

      {/* Pillar 2 — freedom from dependency */}
      <ContentSection
        n="02"
        eyebrow="Freedom from dependency"
        title="The point is control."
        testid="founder-freedom"
        className="bg-secondary/40"
      >
        <p className="text-lg leading-relaxed text-foreground">
          Beyond the Box exists to give firms more freedom — from software vendors, from weak
          supplier dependency, and from fragmented support. Firms should not have to accept
          dependency as the price of operational competence.
        </p>
        <HairlineList
          testid="founder-freedom-list"
          items={[
            "Stop renting your firm from software vendors.",
            "Take back control of your workflow, compliance and data.",
            "Firms should not have to trade control for software.",
          ]}
        />
      </ContentSection>

      {/* Pull statement */}
      <section className="border-b border-border py-24 md:py-28" data-testid="founder-statement">
        <Container>
          <Reveal>
            <p className="max-w-4xl font-display text-3xl font-semibold leading-[1.05] tracking-[-0.02em] text-foreground md:text-5xl">
              Most law firms do not need more software theatre.
              <span className="text-accent"> They need more control.</span>
            </p>
          </Reveal>
        </Container>
      </section>

      <ProductFinalCTA
        testid="founder-final-cta"
        title="See what owner control looks like."
        body="A blunt, practical walkthrough built around your firm — not a generic demo."
        label="Book a walkthrough"
      />
    </div>
  );
}
